import { SiteFooter, SiteHeader } from "@inboxkit/ui";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { API_URL, APP_URL, MARKETING_URL } from "./lib/env";

interface ComponentStatus {
  name: string;
  status: "operational" | "degraded" | "down" | "maintenance";
  note?: string;
  uptime: number[];
  uptimePct: number;
  monitoredFrom: string | null;
  monitoredDays: number;
  monitoredHours: number;
}

interface Incident {
  id: string;
  title: string;
  status: "investigating" | "identified" | "monitoring" | "resolved";
  updates: Array<{
    timestamp: string;
    status: string;
    message: string;
  }>;
}

interface StatusData {
  overall: "operational" | "partial_outage" | "major_outage" | "maintenance";
  components: ComponentStatus[];
  incidents: Incident[];
  lastUpdated: string;
  monitoredFrom: string | null;
}

type DisplayComponent = Omit<ComponentStatus, "status"> & { status: ComponentStatus["status"] | null };

const STORAGE_KEY = "inboxkit-status-last-known";
const BAR_SLOTS = 90;

// The API returns a flat list; group it here for display.
const GROUPS: Array<{ name: string; components: string[] }> = [
  { name: "Platform", components: ["API", "Dashboard", "Marketing"] },
  { name: "Delivery", components: ["Email", "Webhooks"] },
];

// Headline for the overall state. The last word is set in italic serif.
const OVERALL: Record<StatusData["overall"], { lead: string; last: ReactNode; dot: string }> = {
  operational: { lead: "All systems", last: "operational.", dot: "bg-leaf" },
  partial_outage: { lead: "Partial", last: "outage.", dot: "bg-amber-500" },
  major_outage: { lead: "Major", last: "outage.", dot: "bg-red-600" },
  maintenance: { lead: "Scheduled", last: "maintenance.", dot: "bg-blue-600" },
};

const STATUS_LABEL: Record<ComponentStatus["status"], string> = {
  operational: "Operational",
  degraded: "Degraded",
  down: "Offline",
  maintenance: "Maintenance",
};

const PENDING_COMPONENTS: DisplayComponent[] = ["API", "Dashboard", "Marketing", "Email", "Webhooks"].map((name) => ({
  name,
  status: null,
  uptime: [],
  uptimePct: 0,
  monitoredFrom: null,
  monitoredDays: 0,
  monitoredHours: 0,
}));

function groupComponents(components: DisplayComponent[]) {
  const known = new Set(GROUPS.flatMap((g) => g.components));
  const groups = GROUPS.map((g) => ({
    name: g.name,
    components: components.filter((c) => g.components.includes(c.name)),
  }));
  const other = components.filter((c) => !known.has(c.name));
  if (other.length > 0) groups.push({ name: "Other", components: other });
  return groups.filter((g) => g.components.length > 0);
}

function barColor(value: number) {
  if (value < 0) return "bg-paper-3";
  if (value === 100) return "bg-leaf";
  if (value >= 99) return "bg-amber-400";
  return "bg-red-500";
}

function barTitle(value: number, daysAgo: number) {
  const when = daysAgo === 0 ? "Today" : daysAgo === 1 ? "Yesterday" : `${daysAgo} days ago`;
  return value < 0 ? `${when}: no data` : `${when}: ${value.toFixed(2)}% uptime`;
}

function StatusIcon({ status }: { status: DisplayComponent["status"] }) {
  const color =
    status === null
      ? "text-mute/50"
      : status === "operational"
        ? "text-leaf"
        : status === "degraded"
          ? "text-amber-500"
          : status === "down"
            ? "text-red-600"
            : "text-blue-600";
  return (
    <svg className={`h-3.5 w-3.5 shrink-0 ${color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" strokeWidth="2" />
      {status === "operational" || status === null ? (
        <path d="M8.5 12.5l2.5 2.5 4.5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M12 8v5m0 3h.01" strokeWidth="2" strokeLinecap="round" />
      )}
    </svg>
  );
}

function UptimeStrip({ uptime }: { uptime: number[] }) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Show a fixed window; left-pad with "no data" so the newest day is always at the right edge.
  const slots = isMobile ? 30 : BAR_SLOTS;
  const recent = uptime.slice(-slots);
  const values = [...Array(slots - recent.length).fill(-1), ...recent];

  return (
    <div className="flex h-5 gap-[2px]" role="img" aria-label={`Uptime over the last ${slots} days`}>
      {values.map((value, i) => (
        <div
          key={i}
          className={`flex-1 rounded-[1px] transition-opacity hover:opacity-70 ${barColor(value)}`}
          title={barTitle(value, slots - 1 - i)}
        />
      ))}
    </div>
  );
}

function ComponentRow({ component }: { component: DisplayComponent }) {
  const [open, setOpen] = useState(false);
  const statusColor =
    component.status === "degraded"
      ? "text-amber-600"
      : component.status === "down"
        ? "text-red-600"
        : component.status === "maintenance"
          ? "text-blue-600"
          : "text-mute";

  return (
    <div className="border-t border-rule px-4 py-3">
      <div className="mb-2 flex items-center gap-2">
        <StatusIcon status={component.status} />
        <h3 className="flex-1 truncate text-sm font-medium text-ink">{component.name}</h3>
        <span className={`text-xs ${statusColor}`}>{component.status ? STATUS_LABEL[component.status] : "—"}</span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex h-5 w-5 items-center justify-center rounded text-mute/80 hover:bg-paper-2 hover:text-ink"
          aria-expanded={open}
          aria-label={`${open ? "Hide" : "Show"} details for ${component.name}`}
        >
          <PlusMinus open={open} />
        </button>
      </div>
      <UptimeStrip uptime={component.uptime} />
      {open && (
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 rounded-md bg-paper-2/60 px-3 py-2 text-xs text-ink-soft sm:grid-cols-3">
          <div>
            <dt className="text-mute/80">Uptime</dt>
            <dd className="font-medium text-ink">{component.status ? `${component.uptimePct.toFixed(2)}%` : "—"}</dd>
          </div>
          <div>
            <dt className="text-mute/80">Monitored since</dt>
            <dd className="font-medium text-ink">
              {component.monitoredFrom ? new Date(component.monitoredFrom).toLocaleDateString() : "—"}
            </dd>
          </div>
          {component.note && (
            <div className="col-span-2 sm:col-span-1">
              <dt className="text-mute/80">Note</dt>
              <dd className="font-medium text-ink">{component.note}</dd>
            </div>
          )}
        </dl>
      )}
    </div>
  );
}

function PlusMinus({ open }: { open: boolean }) {
  return (
    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12h14" strokeWidth="2" strokeLinecap="round" />
      {!open && <path d="M12 5v14" strokeWidth="2" strokeLinecap="round" />}
    </svg>
  );
}

function ComponentGroup({ name, components, forceOpen }: { name: string; components: DisplayComponent[]; forceOpen: boolean }) {
  const [collapsed, setCollapsed] = useState(false);
  const isOpen = forceOpen || !collapsed;
  const maxDays = Math.min(BAR_SLOTS, Math.max(...components.map((c) => c.monitoredDays), 0));

  return (
    <section className="sheet overflow-hidden rounded-2xl" aria-label={name}>
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="flex w-full items-center gap-2 bg-paper-2/60 px-4 py-2.5 text-left hover:bg-paper-2"
        aria-expanded={isOpen}
      >
        <svg
          className={`h-3.5 w-3.5 text-mute/80 transition-transform ${isOpen ? "" : "-rotate-90"}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <h2 className="flex-1 text-sm font-medium text-ink">{name}</h2>
        <span className="text-xs text-mute">
          {components.length} {components.length === 1 ? "service" : "services"}
        </span>
      </button>
      {isOpen && (
        <>
          {components.map((component) => (
            <ComponentRow key={component.name} component={component} />
          ))}
          <div className="flex justify-between border-t border-rule px-4 py-2 text-xs text-mute">
            <span>
              <span className="sm:hidden">30 days ago</span>
              <span className="hidden sm:inline">{BAR_SLOTS} days ago</span>
            </span>
            {maxDays < BAR_SLOTS && <span className="hidden text-mute/80 sm:inline">Monitoring started {maxDays === 0 ? "today" : `${maxDays} days ago`}</span>}
            <span>Today</span>
          </div>
        </>
      )}
    </section>
  );
}

function SummaryCard({ label, count, color }: { label: string; count: number | null; color: string }) {
  return (
    <div className="sheet rounded-2xl px-3 py-4 text-center">
      <div className="font-mono text-[10.5px] tracking-[0.14em] text-mute uppercase">{label}</div>
      <div className={`mt-1 font-serif text-[40px] leading-none tabular-nums ${count ? color : "text-mute/60"}`}>{count ?? "—"}</div>
    </div>
  );
}

function IncidentCard({ incident }: { incident: Incident }) {
  const statusColors = {
    investigating: "text-yellow-700 bg-yellow-50",
    identified: "text-orange-700 bg-orange-50",
    monitoring: "text-blue-700 bg-blue-50",
    resolved: "text-green-700 bg-green-50",
  };

  return (
    <div className="border-t border-rule px-4 py-4 first:border-t-0">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <h3 className="text-sm font-medium text-ink">{incident.title}</h3>
        <span className={`shrink-0 self-start rounded px-2 py-0.5 text-xs font-medium ${statusColors[incident.status]}`}>
          {incident.status.charAt(0).toUpperCase() + incident.status.slice(1)}
        </span>
      </div>
      <div className="space-y-3">
        {incident.updates.map((update, i) => (
          <div key={i} className="border-l-2 border-rule pl-3">
            <div className="mb-1 flex flex-col gap-1 text-xs text-ink-soft sm:flex-row sm:items-center sm:gap-2">
              <span className="font-medium">{update.status}</span>
              <span className="hidden sm:inline" aria-hidden="true">·</span>
              <time dateTime={update.timestamp}>{new Date(update.timestamp).toLocaleString()}</time>
            </div>
            <p className="text-sm text-ink">{update.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function App() {
  const [status, setStatus] = useState<StatusData | null>(() => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/v1/status`);
        if (!response.ok) throw new Error("Failed to fetch status");
        const data = await response.json();
        setStatus(data);
        setError(null);
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch {
          // ignore storage errors
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  const components: DisplayComponent[] = status?.components || PENDING_COMPONENTS;
  const count = (s: ComponentStatus["status"]) => (status ? components.filter((c) => c.status === s).length : null);
  const needle = query.trim().toLowerCase();
  const groups = groupComponents(needle ? components.filter((c) => c.name.toLowerCase().includes(needle)) : components);

  return (
    <div className="paper-grain flex min-h-screen flex-col bg-paper text-ink">
      <SiteHeader homeUrl={MARKETING_URL} label="Status" signInUrl={APP_URL} solid />

      <main className="paper-light flex w-full flex-1 justify-center">
        <div className="w-full max-w-3xl px-4 pt-32 pb-20 sm:px-6">
          <div className="mb-12 text-center">
            <div className="glass mb-7 inline-flex items-center gap-2 rounded-full px-3 py-1 font-mono text-[11px] text-ink-soft">
              <span className={`h-1.5 w-1.5 rounded-full ${status ? OVERALL[status.overall].dot : "bg-mute/50"} ${status?.overall === "operational" ? "animate-pulse-dot" : ""}`} />
              {status?.lastUpdated ? `Updated ${new Date(status.lastUpdated).toLocaleTimeString()}` : "Checking…"}
            </div>
            <h1 className="font-serif text-[56px] leading-[0.95] tracking-[-0.02em] text-ink sm:text-[80px]">
              {status ? OVERALL[status.overall].lead : "Checking"}{" "}
              <span className="italic">{status ? OVERALL[status.overall].last : "status…"}</span>
            </h1>
            <p className="mx-auto mt-5 max-w-md text-ink-soft">
              Live status and 90-day uptime for all {components.length} InboxKit services.
            </p>
          </div>

          {error && !status && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm" role="alert">
              <div className="font-medium text-red-900">Error loading status</div>
              <div className="text-red-700">{error}</div>
            </div>
          )}

          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <SummaryCard label="Operational" count={count("operational")} color="text-leaf" />
            <SummaryCard label="Degraded" count={count("degraded")} color="text-amber-500" />
            <SummaryCard label="Offline" count={count("down")} color="text-red-600" />
            <SummaryCard label="Maintenance" count={count("maintenance")} color="text-blue-700" />
          </div>

          <div className="relative mb-5">
            <svg
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mute/80"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" strokeWidth="2" />
              <path d="M20 20l-3.5-3.5" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search services"
              aria-label="Search services"
              className="w-full rounded-xl border border-rule-strong bg-field py-2.5 pl-9 pr-3 text-sm text-ink placeholder:text-mute/70 focus:border-ink focus:outline-none focus:ring-4 focus:ring-ink/10"
            />
          </div>

          <div className="mb-12 space-y-4">
            {groups.length > 0 ? (
              groups.map((group) => (
                <ComponentGroup key={group.name} name={group.name} components={group.components} forceOpen={needle !== ""} />
              ))
            ) : (
              <p className="sheet rounded-2xl py-8 text-center text-sm text-mute">
                No services match “{query}”
              </p>
            )}
          </div>

          <section aria-labelledby="incidents-heading">
            <h2 id="incidents-heading" className="mb-4 font-serif text-[32px] leading-none text-ink">
              Past <span className="italic">incidents</span>
            </h2>
            {status && status.incidents.length > 0 ? (
              <div className="sheet overflow-hidden rounded-2xl">
                {status.incidents.map((incident) => (
                  <IncidentCard key={incident.id} incident={incident} />
                ))}
              </div>
            ) : (
              <p className="sheet rounded-2xl py-8 text-center text-sm text-mute">No incidents reported</p>
            )}
          </section>
        </div>
      </main>

      <SiteFooter
        columns={[
          { title: "Product", links: [{ label: "Overview", href: MARKETING_URL }] },
          { title: "Developers", links: [{ label: "API", href: `${MARKETING_URL}/#api` }] },
          { title: "Account", links: [{ label: "Sign in", href: APP_URL }] },
        ]}
      />
    </div>
  );
}
